import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  MapPin,
  DollarSign,
  Home,
  Calendar,
  Ruler,
  Pencil,
  Check,
} from 'lucide-react';
import HouseReactions from './HouseReactions.jsx';
import ShareButton from '../share/ShareButton.jsx';
import { useHousesStore } from '../../store/useStore.js';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="p-4 bg-white border border-indigo-100 rounded-lg shadow-sm">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <Icon className="w-4 h-4 text-indigo-500" />
        {label}
      </div>
      <p className="mt-2 text-lg font-semibold text-gray-800">{value ?? '—'}</p>
    </div>
  );
}

export default function HouseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    houses,
    loadHouseById,
    updateHouse,
    deleteHouse,
  } = useHousesStore();

  const house = houses.find((item) => item.id === id);

  const [loading, setLoading] = useState(!house);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [formState, setFormState] = useState({
    address: house?.address || '',
    price: house?.price || '',
    url: house?.url || '',
  });

  useEffect(() => {
    if (!house) {
      setLoading(true);
      loadHouseById(id)
        .catch((err) => setLoadError(err.message || 'Unable to load house.'))
        .finally(() => setLoading(false));
    }
  }, [house, id, loadHouseById]);

  useEffect(() => {
    if (house) {
      setFormState({
        address: house.address || '',
        price: house.price || '',
        url: house.url || '',
      });
    }
  }, [house?.address, house?.price, house?.url]);

  const analysis = house?.analysis_data;

  const analysisStats = useMemo(() => {
    if (!analysis) return [];
    return [
      {
        icon: DollarSign,
        label: 'Total Monthly Cost',
        value: analysis.totalMonthly ? currency.format(analysis.totalMonthly) : null,
      },
      {
        icon: Home,
        label: 'Down Payment',
        value: analysis.downPaymentAmount ? currency.format(analysis.downPaymentAmount) : null,
      },
      {
        icon: Calendar,
        label: 'Estimated Closing Costs',
        value: analysis.estimatedClosingCosts ? currency.format(analysis.estimatedClosingCosts) : null,
      },
      {
        icon: Ruler,
        label: 'Loan Amount',
        value: analysis.loanAmount ? currency.format(analysis.loanAmount) : null,
      },
    ];
  }, [analysis]);

  const handleSave = async () => {
    if (!house) return;
    setSaving(true);
    setSavedMessage('');
    setActionError('');

    try {
      const parsedPrice = formState.price === '' ? undefined : Number(formState.price);
      const payload = {
        address: formState.address,
        url: formState.url,
      };

      if (parsedPrice !== undefined && !Number.isNaN(parsedPrice)) {
        payload.price = parsedPrice;
      }

      await updateHouse(house.id, payload);
      await loadHouseById(house.id);
      setSavedMessage('Saved!');
      setTimeout(() => setSavedMessage(''), 2500);
    } catch (err) {
      setActionError(err.message || 'Unable to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!house) return;
    if (!window.confirm('Remove this house from your saved list?')) {
      return;
    }

    setDeleting(true);
    setActionError('');
    try {
      await deleteHouse(house.id);
      navigate('/dashboard');
    } catch (err) {
      setActionError(err.message || 'Unable to delete house.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          Loading house details…
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 px-4 text-center">
        <p className="text-lg text-red-600 font-semibold mb-2">{loadError}</p>
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="text-indigo-600 font-semibold hover:text-indigo-700"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  if (!house) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-10">
      <div className="max-w-5xl mx-auto px-4 space-y-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <header className="bg-white rounded-xl shadow-lg border border-indigo-100 p-6 space-y-4">
          {actionError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {actionError}
            </div>
          )}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-indigo-500">
                <MapPin className="w-4 h-4" />
                {house.address || 'Address TBD'}
              </p>
              <h1 className="text-3xl font-bold text-gray-800">
                {house.price ? currency.format(house.price) : 'Price not set'}
              </h1>
              {house.url && (
                <a
                  href={house.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  View listing
                </a>
              )}
            </div>

            <div className="w-full md:w-72">
              <div className="space-y-3">
                <label className="text-xs uppercase font-semibold tracking-wide text-gray-500">Edit details</label>
                <input
                  type="text"
                  value={formState.address}
                  onChange={(event) => setFormState((v) => ({ ...v, address: event.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="123 Main St"
                />
                <input
                  type="number"
                  value={formState.price}
                  onChange={(event) => setFormState((v) => ({ ...v, price: event.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="List price"
                />
                <input
                  type="url"
                  value={formState.url}
                  onChange={(event) => setFormState((v) => ({ ...v, url: event.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Listing URL"
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
                    Save Changes
                  </button>
                  {savedMessage && (
                    <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      {savedMessage}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-2">
          {analysisStats.map((stat) => (
            <Stat key={stat.label} icon={stat.icon} label={stat.label} value={stat.value} />
          ))}
        </section>

        <section className="bg-white border border-indigo-100 rounded-xl shadow-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">Cost Breakdown</h2>
          {analysis ? (
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 text-sm">
              <div>
                <dt className="text-gray-500">Principal & Interest</dt>
                <dd className="font-semibold text-gray-800">{currency.format(analysis.principalInterest)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Property Tax (monthly)</dt>
                <dd className="font-semibold text-gray-800">{currency.format(analysis.monthlyTax)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Insurance (monthly)</dt>
                <dd className="font-semibold text-gray-800">{currency.format(analysis.monthlyInsurance)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">HOA (monthly)</dt>
                <dd className="font-semibold text-gray-800">{currency.format(analysis.monthlyHOA)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">PMI</dt>
                <dd className="font-semibold text-gray-800">{currency.format(analysis.monthlyPMI)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Annual income needed</dt>
                <dd className="font-semibold text-gray-800">{currency.format(analysis.annualIncomeNeeded)}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-gray-500">Run a new analysis to see detailed costs.</p>
          )}
        </section>

        <section className="bg-white border border-indigo-100 rounded-xl shadow-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">Share with your household</h2>
          <ShareButton houseId={house.id} />
        </section>

        <section className="bg-white border border-indigo-100 rounded-xl shadow-lg p-6">
          <HouseReactions house={house} />
        </section>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-2"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Remove from saved houses
          </button>
        </div>
      </div>
    </div>
  );
}
